import classNames from 'classnames';
import React, { CSSProperties, SVGProps, useCallback, useEffect, useState } from 'react';

interface ISvgIconProps {
  IconComponent: React.FC<SVGProps<SVGSVGElement>>;
  className?: string;
  wrapperClass?: string;
  width?: string;
  height?: string;
  defaultFill?: string;
  hoverFill?: string;
  activeFill?: string;
  defaultBackground?: string;
  hoverBackground?: string;
  activeBackground?: string;
  stroke?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

type IconProps = {
  background?: string;
  fill?: string;
  width?: string;
  height?: string;
  stroke?: string;
};

export const SvgIcon: React.FC<ISvgIconProps> = ({
  IconComponent,
  defaultFill,
  hoverFill = defaultFill,
  activeFill = defaultFill,
  defaultBackground = 'transparent',
  hoverBackground = defaultBackground,
  activeBackground = defaultBackground,
  width,
  height,
  className = '',
  wrapperClass,
  stroke,
  onClick,
}) => {
  const [fill, setFill] = useState(defaultFill);
  const [background, setBackground] = useState(defaultBackground);

  const handleMouseEnter = useCallback(() => {
    setFill(hoverFill);
    setBackground(hoverBackground);
  }, [hoverFill, hoverBackground]);

  const handleMouseLeave = useCallback(() => {
    setFill(defaultFill);
    setBackground(defaultBackground);
  }, [defaultFill, defaultBackground]);

  const handleMouseDown = useCallback(() => {
    setFill(activeFill);
    setBackground(activeBackground);
  }, [activeFill, activeBackground]);

  const handleMouseUp = useCallback(() => {
    setFill(hoverFill);
    setBackground(hoverBackground);
  }, [hoverFill, hoverBackground]);

  useEffect(() => {
    setFill(defaultFill);
  }, [defaultFill]);

  if (!IconComponent) {
    return null;
  }

  // Safari/WebKit sizes an svg that only has a viewBox (no width/height attrs) to 0x0 inside
  // the flex wrapper below, so the icon is invisible; make the svg fill the sized wrapper
  const iconSizingClass = '[&>svg:not([width])]:w-full [&>svg:not([height])]:h-full';

  const iconProps: IconProps = {
    background,
    fill,
    stroke,
  };
  const divStyle: CSSProperties = {};
  if (width !== undefined) {
    iconProps.width = width;
    divStyle.maxWidth = width;
  }
  if (height !== undefined) {
    iconProps.height = height;
    divStyle.maxHeight = height;
  }
  divStyle.verticalAlign = 'sub';
  divStyle.lineHeight = 'normal';

  if (wrapperClass) {
    return (
      <span className={classNames('inline-flex justify-center items-center', wrapperClass)}>
        <div
          className={classNames(iconSizingClass, className)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          style={divStyle}
          onClick={onClick}
        >
          <IconComponent {...iconProps} />
        </div>
      </span>
    );
  }

  return (
    <div
      className={`inline-flex items-center justify-center ${iconSizingClass} ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      style={divStyle}
      onClick={onClick}
    >
      <IconComponent {...iconProps} />
    </div>
  );
};
